'use client';

import React, { useState, useEffect, Suspense } from 'react';
import logger from '@/lib/logger';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { Mail, Lock, ArrowRight, ArrowLeft, RefreshCw, Phone } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { getSiteUrl } from '@/lib/auth/site-url';
import { EngageIQLogo } from '@/components/brand/EngageIQBrand';

function CitizenLoginContent() {
    const router = useRouter();
    const searchParams = useSearchParams();

    // Read callId from URL params (e.g., /citizen/login?callId=abc123)
    const callId = searchParams.get('callId');
    const inviteToken = searchParams.get('inviteToken');
    const hasCallId = !!callId;
    const inviteSuffix = inviteToken ? `?inviteToken=${encodeURIComponent(inviteToken)}` : '';

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [showResendOption, setShowResendOption] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [resendSuccess, setResendSuccess] = useState(false);
    const [isResending, setIsResending] = useState(false);

    // Cooldown timer
    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCooldown]);

    const handleResendVerification = async () => {
        if (!email || resendCooldown > 0) return;

        setIsResending(true);
        setResendSuccess(false);

        try {
            const supabase = createClient();
            // Use centralized site URL utility for security (env var with fallback)
            const siteUrl = getSiteUrl();

            // Include callId in the redirect URL if present
            const nextUrl = callId
                ? `/calls/${callId}${inviteSuffix}`
                : '/citizen';
            const { error: resendError } = await supabase.auth.resend({
                type: 'signup',
                email,
                options: {
                    emailRedirectTo: `${siteUrl}/auth/callback?type=citizen&next=${encodeURIComponent(nextUrl)}`,
                },
            });

            if (resendError) {
                setError(`Failed to resend: ${resendError.message}`);
            } else {
                setResendSuccess(true);
                setResendCooldown(60); // 60 second cooldown
                setError(''); // Clear the error since we successfully resent
            }
        } catch (err) {
            setError('Failed to resend verification email. Please try again.');
            logger.error('Page', 'Resend error', { err });
        } finally {
            setIsResending(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!email || !password) {
            setError('Email and password are required');
            return;
        }

        setIsLoading(true);

        try {
            const supabase = createClient();

            const { data, error: signInError } = await supabase.auth.signInWithPassword({
                email,
                password,
            });

            if (signInError) {
                if (signInError.message.includes('Invalid login credentials')) {
                    setError('Invalid email or password');
                    setShowResendOption(false);
                } else if (signInError.message.includes('Email not confirmed')) {
                    setError('Please verify your email before signing in. Check your inbox for the verification link.');
                    setShowResendOption(true);
                } else {
                    setError(signInError.message);
                    setShowResendOption(false);
                }
                return;
            }

            // Success - user logged in via citizen portal, keep them here
            // Note: Any user (citizen or officer) can access citizen portal intentionally
            // Role-based redirects have been removed to allow officers to test/view citizen experience
            if (callId) {
                router.push(`/calls/${callId}${inviteSuffix}`);
            } else {
                router.push('/citizen');
            }
        } catch (err) {
            setError('An unexpected error occurred. Please try again.');
            logger.error('Page', 'Login error', { err });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <main className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-6">
            {/* Background Ambience */}
            <div className="fixed inset-0 pointer-events-none">
                <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-emerald-600/10 rounded-full blur-[128px]" />
                <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[128px]" />
            </div>

            <div className="relative z-10 w-full max-w-lg">
                {/* Header */}
                <div className="text-center mb-8">
                    <Link href="/citizen" className="inline-flex items-center gap-2 mb-6 hover:opacity-80 transition-opacity">
                        <EngageIQLogo theme="dark" width={150} height={41} style={{ width: 'auto', height: '30px' }} />
                        <span className="text-emerald-400 font-normal">Citizen</span>
                    </Link>
                    <h1 className="text-3xl font-bold mb-2">Welcome Back</h1>
                    <p className="text-zinc-400">Sign in to access your reports</p>
                </div>

                {/* Call Banner - shown when joining via share link */}
                {hasCallId && (
                    <div className="mb-6 p-4 rounded-xl bg-blue-500/10 border border-blue-500/30 backdrop-blur-sm">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-blue-500/20">
                                <Phone className="text-blue-400 w-5 h-5" />
                            </div>
                            <div className="text-left">
                                <p className="text-blue-200 font-medium">Joining a call</p>
                                <p className="text-blue-300/70 text-sm">Sign in to continue to your call</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm space-y-5">
                    {/* Success message for resend */}
                    {resendSuccess && (
                        <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm">
                            Verification email sent! Check your inbox.
                        </div>
                    )}

                    {/* Error message */}
                    {error && (
                        <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm">
                            {error}
                        </div>
                    )}

                    {/* Resend verification option */}
                    {showResendOption && !resendSuccess && (
                        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                            <p className="text-amber-400 text-sm mb-2">
                                Didn&apos;t receive the email? You can request a new one.
                            </p>
                            <button
                                type="button"
                                onClick={handleResendVerification}
                                disabled={isResending || resendCooldown > 0}
                                className="flex items-center gap-2 text-sm text-amber-300 hover:text-amber-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <RefreshCw size={14} className={isResending ? 'animate-spin' : ''} />
                                {isResending
                                    ? 'Sending...'
                                    : resendCooldown > 0
                                    ? `Resend available in ${resendCooldown}s`
                                    : 'Resend verification email'}
                            </button>
                        </div>
                    )}

                    {/* Email */}
                    <div>
                        <label className="block text-sm text-zinc-400 mb-2">Email Address</label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 w-5 h-5" />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                required
                                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-emerald-500/50 placeholder-zinc-600"
                            />
                        </div>
                    </div>

                    {/* Password */}
                    <div>
                        <label className="block text-sm text-zinc-400 mb-2">Password</label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 w-5 h-5" />
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter your password"
                                required
                                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-emerald-500/50 placeholder-zinc-600"
                            />
                        </div>
                    </div>

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2"
                    >
                        {isLoading ? 'Signing In...' : 'Sign In'}
                        {!isLoading && <ArrowRight size={18} />}
                    </button>

                    {/* Register link */}
                    <p className="text-center text-sm text-zinc-400">
                        Don&apos;t have an account?{' '}
                        <Link
                            href={callId
                                ? `/citizen/register?callId=${encodeURIComponent(callId)}${inviteToken ? `&inviteToken=${encodeURIComponent(inviteToken)}` : ''}`
                                : '/citizen/register'}
                            className="text-emerald-400 hover:text-emerald-300 transition-colors"
                        >
                            Create one
                        </Link>
                    </p>
                </form>

                {/* Dev Mode Test Credentials */}
                {process.env.NODE_ENV === 'development' && (
                    <div className="mt-6 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                        <p className="text-xs font-medium text-amber-400 mb-2">🧪 Test Credentials (Dev Mode)</p>
                        <div className="text-xs text-amber-300/80">
                            <p><strong>Citizen:</strong> citizen@test.local / password123</p>
                        </div>
                    </div>
                )}

                {/* Back link */}
                <div className="mt-6 text-center">
                    <Link
                        href={callId
                            ? `/citizen?callId=${encodeURIComponent(callId)}${inviteToken ? `&inviteToken=${encodeURIComponent(inviteToken)}` : ''}`
                            : '/citizen'}
                        className="inline-flex items-center gap-2 text-zinc-500 hover:text-zinc-300 transition-colors text-sm"
                    >
                        <ArrowLeft size={16} />
                        Back to Citizen Portal
                    </Link>
                </div>
            </div>
        </main>
    );
}

// Main export with Suspense boundary for useSearchParams
export default function CitizenLogin() {
    return (
        <Suspense fallback={
            <main className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">
                <div className="text-zinc-400">Loading...</div>
            </main>
        }>
            <CitizenLoginContent />
        </Suspense>
    );
}
